import { EnvironmentOutlined, PhoneOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Alert, Descriptions, Empty, Space, Spin, Table, Tabs, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { apiFetch } from "../api/client";
import type { CandidateInfo, CandidateInfoResponse } from "../api/types";

/**
 * The panel beside the candidate list: everything about one speaker/talk pair
 * that the coordinator would otherwise have to look up on three other screens.
 *
 * The old application kept the same material in a collapsed sidebar with three
 * tabs — the details, "who else gave this talk here", and "when was this
 * speaker here". The tabs are kept; the details are the panel's body because
 * they are what the coordinator reads while deciding.
 *
 * One request answers all of it (…/candidates/:id/info); the distance in it
 * costs a call to a routing service, so the panel is only asked once a row is
 * actually selected.
 */
export function CandidateInfoPanel({
  weekBegin,
  speakerSpeechId,
  placeholder,
}: {
  /**
   * Both halves can be the varying one: the talk assignment screen fixes the
   * week and lets the candidate change, the speaker search fixes the pair and
   * lets the week change. Either being null means nothing is asked for yet.
   */
  weekBegin: string | null;
  speakerSpeechId: number | null;
  /** What to say while the missing half has not been picked. */
  placeholder: string;
}) {
  const { t } = useTranslation();
  const ready = weekBegin !== null && speakerSpeechId !== null;

  const info = useQuery({
    queryKey: ["local_assignation", "candidate_info", weekBegin, speakerSpeechId],
    queryFn: () =>
      apiFetch<CandidateInfoResponse>(
        `/local_assignation/events/${weekBegin}/candidates/${speakerSpeechId}/info`,
      ),
    enabled: ready,
  });

  if (!ready) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={placeholder}
        style={{ marginTop: 48 }}
      />
    );
  }

  if (info.isPending) return <Spin style={{ marginTop: 48 }} />;

  if (info.error) {
    return (
      <Alert
        type="error"
        showIcon
        title={info.error instanceof Error ? info.error.message : String(info.error)}
      />
    );
  }

  const data = info.data!.data;

  return (
    <Tabs
      size="small"
      items={[
        {
          key: "info",
          label: t("local_assignation.search_speaker_info"),
          children: <Details data={data} />,
        },
        {
          key: "speaker",
          // The old tabs were labelled with the speaker and the schema
          // themselves, which is shorter and says more than a generic heading.
          label: [data.speaker.first_name, data.speaker.last_name].filter(Boolean).join(" "),
          children: <SpeakerHistory data={data} />,
        },
        {
          key: "speech",
          label: `${t("activerecord.attributes.speech.schema")} ${data.speech.schema ?? ""}`.trim(),
          children: <SpeechHistory data={data} />,
        },
      ]}
    />
  );
}

/** A row of the description lists; empty values are left out entirely. */
function rows(entries: [string, string | number | null | undefined][]) {
  return entries
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([label, value]) => ({ key: label, label, children: value }));
}

function Details({ data }: { data: CandidateInfo }) {
  const { t } = useTranslation();

  const congregation = data.congregation;
  const distanceKnown = data.distance && data.distance !== "?";

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      {/* What the server would ask about before assigning — the same list the
          confirmation dialog shows, but here it is visible beforehand. */}
      {data.considerations.map((item) => (
        <Alert key={item.key} type="warning" showIcon title={item.title} description={item.text} />
      ))}

      <Descriptions
        size="small"
        column={1}
        title={t("activerecord.models.speaker")}
        items={rows([
          [t("activerecord.attributes.speaker.first_name"), data.speaker.first_name],
          [t("activerecord.attributes.speaker.last_name"), data.speaker.last_name],
          [t("activerecord.attributes.user.zip_code"), data.speaker.zip_code],
          [t("activerecord.attributes.user.country"), data.speaker.country],
          [t("activerecord.attributes.user.phone"), data.speaker.phone],
          [t("activerecord.attributes.user.mobile"), data.speaker.mobile],
          [t("activerecord.attributes.user.private_email"), data.speaker.private_email],
          [t("link.distance"), distanceKnown ? data.distance : null],
          [t("link.drive_time"), distanceKnown ? data.drive_time : null],
          [t("activerecord.attributes.speaker.max_km_range"), data.speaker.max_km_range],
          [
            t("activerecord.attributes.speaker.min_weeks_in_advance"),
            data.speaker.min_weeks_in_advance,
          ],
        ])}
        extra={
          data.route_url ? (
            <Typography.Link href={data.route_url} target="_blank" rel="noreferrer">
              <Space size={4}>
                <EnvironmentOutlined />
                {t("link.googlemap")}
              </Space>
            </Typography.Link>
          ) : null
        }
      />

      {data.speaker.contact_me && (
        <Space size={4}>
          <PhoneOutlined style={{ color: "#8d8d8d" }} />
          <Typography.Text type="secondary">{t("speaker.contact_me")}</Typography.Text>
        </Space>
      )}

      <Descriptions
        size="small"
        column={1}
        title={t("activerecord.models.speech")}
        items={rows([
          [t("activerecord.attributes.speech.schema"), data.speech.schema],
          [t("activerecord.attributes.speech.title"), data.speech.title],
          [t("activerecord.models.language"), data.speech.language_code],
          [
            t("activerecord.attributes.speaker_speech.additional_notes"),
            data.speech.additional_notes,
          ],
        ])}
      />

      {congregation && (
        <Descriptions
          size="small"
          column={1}
          title={t("activerecord.models.congregation")}
          items={rows([
            [t("activerecord.attributes.congregation.denomination"), congregation.denomination],
            [
              t("activerecord.attributes.congregation.congregation_code"),
              congregation.congregation_code,
            ],
            [t("activerecord.models.circumscription"), congregation.circumscription_code],
            [
              t("congregation.headers.contact_person"),
              [congregation.contact_first_name, congregation.contact_last_name]
                .filter(Boolean)
                .join(" "),
            ],
            [t("activerecord.attributes.congregation.phone"), congregation.phone],
            [t("activerecord.attributes.congregation.mobile"), congregation.mobile],
            [t("activerecord.attributes.congregation.email"), congregation.email],
            [t("activerecord.attributes.congregation.street_number"), congregation.street_number],
            [t("activerecord.attributes.congregation.zip_code"), congregation.zip_code],
            [t("activerecord.attributes.congregation.city"), congregation.city],
            [t("activerecord.attributes.congregation.state"), congregation.state],
            [t("activerecord.attributes.congregation.country"), congregation.country],
            [
              t("activerecord.attributes.congregation.congregation_phone"),
              congregation.congregation_phone,
            ],
          ])}
        />
      )}

      {/* Who to call in that congregation — the old panel listed them last, and
          for the same reason: this is the fallback when the speaker is not
          reachable. */}
      {data.coordinators.map((item, index) => (
        <Descriptions
          key={`${item.private_email ?? ""}-${index}`}
          size="small"
          column={1}
          title={t("activerecord.models.local_coordinator")}
          items={rows([
            [t("activerecord.attributes.local_coordinator.first_name"), item.first_name],
            [t("activerecord.attributes.local_coordinator.last_name"), item.last_name],
            [t("activerecord.attributes.user.phone"), item.phone],
            [t("activerecord.attributes.user.mobile"), item.mobile],
            [t("activerecord.attributes.user.private_email"), item.private_email],
          ])}
        />
      ))}
    </Space>
  );
}

/** When this speaker last spoke here, and with which talk. */
function SpeakerHistory({ data }: { data: CandidateInfo }) {
  const { t } = useTranslation();

  return (
    <Table
      rowKey="week_begin"
      size="small"
      pagination={false}
      scroll={{ y: 320 }}
      locale={{ emptyText: t("app.no_data") }}
      dataSource={data.speaker_history}
      columns={[
        {
          title: t("activerecord.attributes.local_assignation.week_begin"),
          dataIndex: "week_label",
          width: 150,
        },
        {
          title: t("activerecord.attributes.speech.schema"),
          dataIndex: "speech_schema",
          width: 70,
          align: "right",
        },
        {
          title: t("activerecord.attributes.speech.title"),
          dataIndex: "speech_title",
          ellipsis: true,
        },
      ]}
    />
  );
}

/** When this talk was last heard here, and who gave it. */
function SpeechHistory({ data }: { data: CandidateInfo }) {
  const { t } = useTranslation();

  return (
    <Table
      rowKey="week_begin"
      size="small"
      pagination={false}
      scroll={{ y: 320 }}
      locale={{ emptyText: t("app.no_data") }}
      dataSource={data.speech_history}
      columns={[
        {
          title: t("activerecord.attributes.local_assignation.week_begin"),
          dataIndex: "week_label",
          width: 150,
        },
        {
          title: t("activerecord.attributes.local_assignation.speaker_id"),
          dataIndex: "speaker_name",
          ellipsis: true,
        },
        {
          title: t("activerecord.models.congregation"),
          dataIndex: "speaker_congregation",
          width: 150,
          ellipsis: true,
          render: (value: string | null, row) =>
            row.speaker_circumscription ? (
              <Space size={4}>
                {value}
                <Tag style={{ marginInlineEnd: 0 }}>{row.speaker_circumscription}</Tag>
              </Space>
            ) : (
              value
            ),
        },
      ]}
    />
  );
}
